import React from 'react';
import PropTypes from 'prop-types';
import styled from 'styled-components';
import { sa } from './tokens';

// The Super Admin data table: rounded bordered container, purple header row, zebra body rows —
// exactly as the Figma "Table" component. Columns are declarative so every screen renders the
// same table with its own column set. `size='sm'` matches the tighter leaderboard variant.
// Pass `pageSize` to paginate client-side (the credits endpoints return every org/school in one
// response, so long lists like Platform Usage / the Leaderboard would otherwise scroll forever).

const Wrap = styled.div`
  width: 100%;
  background: ${sa.color.white};
  border: 1px solid ${sa.color.stroke};
  border-radius: ${sa.radius.md};
  overflow: hidden;
`;

const Table = styled.table`
  width: 100%;
  border-collapse: collapse;
  table-layout: fixed;
  font-family: ${sa.font.family};

  thead th {
    background: ${sa.color.purple};
    color: ${sa.color.white};
    font-weight: ${sa.font.bold};
    font-size: ${(p) => p.$headerSize};
    text-align: left;
    padding: ${(p) => p.$headerPadY} ${sa.space.cell};
    white-space: nowrap;
  }
  tbody td {
    padding: ${(p) => p.$rowPadY} ${sa.space.cell};
    font-size: ${(p) => p.$bodySize};
    font-weight: ${sa.font.regular};
    color: ${sa.color.textGray};
    border-bottom: 1px solid ${sa.color.stroke};
    vertical-align: middle;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }
  tbody tr.sa-alt td {
    background: ${sa.color.rowAlt};
  }
  tbody tr.sa-expand tbody td {   
    padding: 12px; 
    background: ${sa.color.white};
    white-space: normal;
    overflow: visible;
    text-overflow: clip;
  }
  
  tbody tr.sa-expand td {    
    padding: 0;
  }

  .sa-r {
    text-align: right;
  }
  .sa-c {
    text-align: center;
  }
`;

const PagerBar = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  flex-wrap: wrap;
  padding: 10px ${sa.space.cell};
  border-top: 1px solid ${sa.color.stroke};
  background: ${sa.color.white};
  font-size: 12px;
  color: ${sa.color.textGray};
`;

const PageButtons = styled.div`
  display: flex;
  align-items: center;
  gap: 4px;
`;

const PageBtn = styled.button`
  min-width: 30px;
  height: 30px;
  padding: 0 8px;
  border: 1px solid ${(p) => (p.$active ? sa.color.teal : sa.color.stroke)};
  border-radius: ${sa.radius.sm};
  background: ${(p) => (p.$active ? sa.color.teal : sa.color.white)};
  color: ${(p) => (p.$active ? sa.color.white : sa.color.text)};
  font-family: ${sa.font.family};
  font-size: 12px;
  font-weight: ${(p) => (p.$active ? sa.font.bold : sa.font.regular)};
  cursor: pointer;
  &:disabled {
    opacity: 0.45;
    cursor: default;
  }
`;

const Ellipsis = styled.span`
  padding: 0 4px;
  color: ${sa.color.textGray};
`;

const alignClass = (align) => (align === 'right' ? 'sa-r' : align === 'center' ? 'sa-c' : undefined);

// A compact, windowed page list: always the first and last page, the current page and its
// neighbours, and ellipses for the gaps — so 66 pages render as e.g. 1 … 4 5 [6] 7 8 … 66.
function pageWindow(current, total) {
  if (total <= 7) return Array.from({ length: total }, (_, i) => i + 1);
  const pages = new Set([1, total, current, current - 1, current + 1]);
  const sorted = [...pages].filter((p) => p >= 1 && p <= total).sort((a, b) => a - b);
  const out = [];
  let prev = 0;
  sorted.forEach((p) => {
    if (p - prev > 1) out.push(`gap-${p}`);
    out.push(p);
    prev = p;
  });
  return out;
}

function Pager({ page, pageCount, total, from, to, onPage }) {
  return (
    <PagerBar data-testid='data-table-pager'>
      <span>
        Showing {from}–{to} of {total}
      </span>
      <PageButtons>
        <PageBtn type='button' onClick={() => onPage(page - 1)} disabled={page <= 1} aria-label='Previous page'>
          Prev
        </PageBtn>
        {pageWindow(page, pageCount).map((p) =>
          typeof p === 'number' ? (
            <PageBtn key={p} type='button' $active={p === page} aria-current={p === page ? 'page' : undefined} onClick={() => onPage(p)}>
              {p}
            </PageBtn>
          ) : (
            <Ellipsis key={p}>…</Ellipsis>
          )
        )}
        <PageBtn type='button' onClick={() => onPage(page + 1)} disabled={page >= pageCount} aria-label='Next page'>
          Next
        </PageBtn>
      </PageButtons>
    </PagerBar>
  );
}

export default function DataTable({ columns, rows, getRowKey, size, empty, renderExpanded, isRowExpanded, pageSize }) {
  const s =
    size === 'sm'
      ? { headerSize: '12px', headerPadY: '10px', rowPadY: '12px', bodySize: '12px' }
      : { headerSize: '13px', headerPadY: '14px', rowPadY: '16px', bodySize: '13px' };

  const paginated = Boolean(pageSize) && rows.length > pageSize;
  const pageCount = paginated ? Math.ceil(rows.length / pageSize) : 1;
  const [page, setPage] = React.useState(1);

  // Clamp the page whenever the data set shrinks (period change, search, sort) so we never
  // strand the user on a now-empty page past the end.
  React.useEffect(() => {
    setPage((p) => Math.min(Math.max(1, p), pageCount));
  }, [pageCount]);

  const start = paginated ? (page - 1) * pageSize : 0;
  const visibleRows = paginated ? rows.slice(start, start + pageSize) : rows;

  return (
    <Wrap>
      <Table
        $headerSize={s.headerSize}
        $headerPadY={s.headerPadY}
        $rowPadY={s.rowPadY}
        $bodySize={s.bodySize}
      >
        <colgroup>
          {columns.map((c, i) => (
            <col key={c.key || i} style={c.width ? { width: c.width } : undefined} />
          ))}
        </colgroup>
        <thead>
          <tr>
            {columns.map((c, i) => (
              <th key={c.key || i} className={alignClass(c.align)}>
                {c.header ?? c.label}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.length === 0 ? (
            <tr>
              <td
                colSpan={columns.length}
                style={{ textAlign: 'center', color: sa.color.textGray, padding: '28px', whiteSpace: 'normal' }}
              >
                {empty}
              </td>
            </tr>
          ) : (
            visibleRows.map((row, ri) => {
              const absoluteIndex = start + ri;
              const rowKey = getRowKey ? getRowKey(row, absoluteIndex) : absoluteIndex;
              const expanded = Boolean(renderExpanded && isRowExpanded && isRowExpanded(row));
              return (
                <React.Fragment key={rowKey}>
                  <tr className={absoluteIndex % 2 === 1 ? 'sa-alt' : undefined}>
                    {columns.map((c, ci) => (
                      <td key={c.key || ci} className={alignClass(c.align)} style={c.cellStyle}>
                        {c.render ? c.render(row, absoluteIndex) : row[c.key]}
                      </td>
                    ))}
                  </tr>
                  {expanded && (
                    <tr className='sa-expand'>
                      <td colSpan={columns.length}>{renderExpanded(row)}</td>
                    </tr>
                  )}
                </React.Fragment>
              );
            })
          )}
        </tbody>
      </Table>
      {paginated && (
        <Pager
          page={page}
          pageCount={pageCount}
          total={rows.length}
          from={start + 1}
          to={Math.min(start + pageSize, rows.length)}
          onPage={(p) => setPage(Math.min(Math.max(1, p), pageCount))}
        />
      )}
    </Wrap>
  );
}

DataTable.propTypes = {
  columns: PropTypes.arrayOf(
    PropTypes.shape({
      key: PropTypes.string,
      label: PropTypes.node,
      header: PropTypes.node,
      width: PropTypes.string,
      align: PropTypes.oneOf(['left', 'right', 'center']),
      render: PropTypes.func,
      cellStyle: PropTypes.object,
    })
  ).isRequired,
  rows: PropTypes.array.isRequired,
  getRowKey: PropTypes.func,
  size: PropTypes.oneOf(['md', 'sm']),
  empty: PropTypes.node,
  // Inline row expansion: when isRowExpanded(row) is true, a full-width row is rendered
  // beneath it containing renderExpanded(row) — used for the Reveal-History drill-down.
  renderExpanded: PropTypes.func,
  isRowExpanded: PropTypes.func,
  // When set, rows beyond pageSize are paged behind a footer pager instead of all rendered.
  pageSize: PropTypes.number,
};

DataTable.defaultProps = { size: 'md', empty: 'No data for this period.' };
